import React, { useState, useEffect, useMemo } from 'react';
import { getItems, FIG_LISTS } from '../../api/figApi';
import { calculatePolicyAllIn, getPolicyBreakdown, consolidateBreakdown } from '../../utils/invoiceCalc';
import { generateInvoicePDF, defaultLineDescription, DEFAULT_TERMS } from '../../utils/invoicePdf';
import { getAgencySettings } from '../../utils/invoiceSettings';
import { claimInvoiceNumber, writeInvoiceRecord, uploadInvoiceToVault } from '../../utils/invoiceStorage';
import { getBrandAssets } from '../../utils/brandAssets';

const FIELD_STYLE = {
  width: '100%', background: '#111', border: '1px solid #2a2a2a', borderRadius: 4,
  color: '#ccc', fontFamily: "'Share Tech Mono', monospace", fontSize: 12,
  padding: '8px 10px', outline: 'none', boxSizing: 'border-box',
};

// Pre-fill with the exact text the PDF prints by default, so an untouched
// field renders the same invoice as before.
function withDefaultDescription(li) {
  return { ...li, description: defaultLineDescription(li) };
}

/**
 * InvoiceBuilder — review/edit line items before generating an invoice PDF.
 *
 * Props:
 *   policies   — selected policy objects from PolicySelector
 *   insured    — client object (CompanyName, etc.)
 *   getToken   — async token accessor
 *   onGenerate — (invoiceData) => void
 *   onBack     — return to PolicySelector
 *   onClose    — close entire invoice flow
 */
export default function InvoiceBuilder({ policies, insured, getToken, onGenerate, onBack, onClose }) {
  const [job, setJob] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [note, setNote] = useState('');
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [displayMode, setDisplayMode] = useState('bundled');
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Fetch FIG_PolicyFees for all selected policies, compute all-in totals
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const allFees = await getItems(token, FIG_LISTS.POLICY_FEES);
        const policyIds = new Set(policies.map(p => String(p.id)));
        const feesByPolicy = {};
        allFees.forEach(f => {
          const pid = String(f.PolicyId);
          if (policyIds.has(pid)) {
            if (!feesByPolicy[pid]) feesByPolicy[pid] = [];
            feesByPolicy[pid].push(f);
          }
        });
        if (cancelled) return;
        setLineItems(policies.map(p => {
          const fees = feesByPolicy[String(p.id)] || [];
          return {
            policyId: p.id,
            lob: p.LOB || 'Other',
            policyNumber: p.PolicyNumber || p.QuoteNumber || '',
            term: [p.EffectiveDate, p.ExpirationDate].filter(Boolean).join(' – '),
            carrier: p.Carrier || '',
            amount: calculatePolicyAllIn(p, fees),
            breakdown: getPolicyBreakdown(p, fees),
          };
        }).map(withDefaultDescription));
      } catch (err) {
        console.error('[InvoiceBuilder] Failed to load fees:', err.message);
        // Fall back to amounts without additional fees
        if (!cancelled) {
          setLineItems(policies.map(p => ({
            policyId: p.id,
            lob: p.LOB || 'Other',
            policyNumber: p.PolicyNumber || p.QuoteNumber || '',
            term: [p.EffectiveDate, p.ExpirationDate].filter(Boolean).join(' – '),
            carrier: p.Carrier || '',
            amount: calculatePolicyAllIn(p, []),
            breakdown: getPolicyBreakdown(p, []),
          })).map(withDefaultDescription));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [policies, getToken]);

  function updateAmount(index, value) {
    setLineItems(prev => prev.map((li, i) => i === index ? { ...li, amount: Number(value) || 0 } : li));
  }

  function updateDescription(index, value) {
    setLineItems(prev => prev.map((li, i) => i === index ? { ...li, description: value } : li));
  }

  function removeLine(index) {
    setLineItems(prev => prev.filter((_, i) => i !== index));
  }

  const grandTotal = useMemo(() => lineItems.reduce((s, li) => s + li.amount, 0), [lineItems]);

  const hasQuoted = useMemo(
    () => policies.some(p => {
      const st = (p.Status || '').toLowerCase();
      return st !== 'active' && st !== 'bound';
    }),
    [policies]
  );

  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  async function handleGenerate() {
    setGenerating(true);
    try {
      // 1. Build invoice data + fetch settings + claim number
      const invoiceData = {
        client: { id: insured.id, name: insured.CompanyName || '' },
        job,
        referenceNumber,
        note,
        terms,
        displayMode,
        lineItems: lineItems.map(li => ({
          policyId: li.policyId,
          lob: li.lob,
          policyNumber: li.policyNumber,
          term: li.term,
          amount: li.amount,
          breakdown: li.breakdown,
          description: li.description,
        })),
        grandTotal,
        generatedAt: new Date().toISOString(),
      };

      const [settings, invoiceNumber, brandAssets] = await Promise.all([
        getAgencySettings(getToken),
        claimInvoiceNumber(getToken),
        getBrandAssets(getToken),
      ]);
      invoiceData.invoiceNumber = invoiceNumber;

      const pdfBytes = await generateInvoicePDF(invoiceData, insured, settings, invoiceNumber, brandAssets);
      const clientShort = (insured.CompanyName || 'Client').replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
      const filename = `Invoice_${clientShort}_${invoiceNumber}.pdf`;

      // 2. Write to FIG_Invoices (blocking — must succeed to claim number)
      try {
        await writeInvoiceRecord(getToken, invoiceData);
      } catch (err) {
        console.error('[InvoiceBuilder] List write failed:', err);
        alert('Failed to save invoice record — please try again.');
        setGenerating(false);
        return;
      }

      // 3. Trigger browser download (user gets PDF before vault work)
      try {
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('[InvoiceBuilder] Download trigger failed:', err);
      }

      // 4. Upload to FIGVault (best-effort — don't block close)
      const vaultPath = await uploadInvoiceToVault(getToken, invoiceData, pdfBytes, filename);
      if (!vaultPath) {
        alert('Invoice saved but FIGVault upload failed — you can retry from invoice history.');
      }

      // 5. Close modal
      onGenerate(invoiceData);
    } catch (err) {
      console.error('[InvoiceBuilder] Invoice generation failed:', err);
      alert('Failed to generate invoice: ' + err.message);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="FIG-claude-backdrop" onClick={onClose}>
      <div className="FIG-claude-panel" style={{ maxWidth: 660, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        <button className="FIG-claude-close" onClick={onClose}>✕</button>

        {/* Header */}
        <div className="FIG-claude-title" style={{ marginBottom: 2 }}>INVOICE BUILDER</div>
        <div className="FIG-claude-divider" />
        <div style={{ padding: '8px 16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 13, color: '#ccc', fontWeight: 700 }}>
            {insured?.CompanyName || 'Client'}
            {hasQuoted && (
              <span style={{
                border: '1px solid #d4a657', borderRadius: 3, marginLeft: 8,
                padding: '1px 6px', fontSize: 9, fontWeight: 700, color: '#d4a657',
                letterSpacing: '0.5px', verticalAlign: 'middle',
              }}>QUOTED</span>
            )}
          </span>
          <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 11, color: '#666' }}>{today}</span>
        </div>

        {/* Job + Reference */}
        <div style={{ padding: '10px 16px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            value={job}
            onChange={e => setJob(e.target.value)}
            placeholder="Job (e.g. Property Stack, 2026 Renewals)"
            style={{
              width: '100%', background: '#111', border: '1px solid #2a2a2a', borderRadius: 4,
              color: '#ccc', fontFamily: "'Share Tech Mono', monospace", fontSize: 12,
              padding: '8px 10px', outline: 'none', boxSizing: 'border-box',
            }}
          />
          <input
            value={referenceNumber}
            onChange={e => setReferenceNumber(e.target.value)}
            placeholder="Client PO or reference (optional)"
            style={{
              width: '100%', background: '#111', border: '1px solid #2a2a2a', borderRadius: 4,
              color: '#ccc', fontFamily: "'Share Tech Mono', monospace", fontSize: 12,
              padding: '8px 10px', outline: 'none', boxSizing: 'border-box',
            }}
          />
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Note printed above the grand total (optional)"
            rows={3}
            style={{ ...FIELD_STYLE, resize: 'vertical' }}
          />
          <input
            value={terms}
            onChange={e => setTerms(e.target.value)}
            placeholder="Footer terms"
            title="Footer terms"
            style={FIELD_STYLE}
          />
        </div>

        {/* Display mode toggle */}
        <div style={{ padding: '8px 16px 0', display: 'flex', gap: 6 }}>
          {['bundled', 'itemized', 'consolidated'].map(mode => (
            <button key={mode} onClick={() => setDisplayMode(mode)} style={{
              background: displayMode === mode ? '#d4a657' : '#1a1a1a',
              color: displayMode === mode ? '#0f0f0f' : '#888',
              border: '1px solid ' + (displayMode === mode ? '#d4a657' : '#333'),
              borderRadius: 4, fontWeight: 700, fontSize: 10, padding: '5px 12px',
              cursor: 'pointer', fontFamily: "'Share Tech Mono', monospace",
              textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>{mode}</button>
          ))}
        </div>

        {/* Line items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {loading ? (
            <div style={{ color: '#666', fontFamily: "'Share Tech Mono', monospace", fontSize: 12, textAlign: 'center', padding: 24 }}>
              Loading fee data...
            </div>
          ) : lineItems.length === 0 ? (
            <div style={{ color: '#666', fontFamily: "'Share Tech Mono', monospace", fontSize: 12, textAlign: 'center', padding: 24 }}>
              No line items. Go back to select policies.
            </div>
          ) : displayMode === 'bundled' ? (
            /* ---- BUNDLED VIEW ---- */
            lineItems.map((li, idx) => (
              <div key={li.policyId} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                borderBottom: idx < lineItems.length - 1 ? '1px solid #1a1a2e' : 'none',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      background: '#1a1a2e', border: '1px solid #333', borderRadius: 3,
                      padding: '1px 6px', fontSize: 9, fontWeight: 700, color: '#aaa',
                      fontFamily: "'Share Tech Mono', monospace", letterSpacing: '0.5px',
                    }}>{li.lob}</span>
                    <span style={{ fontSize: 12, color: '#ccc', fontFamily: "'Share Tech Mono', monospace", fontWeight: 600 }}>
                      {(li.carrier || '').split(/[\s,]+/).slice(0, 2).join(' ')}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: '#666', fontFamily: "'Share Tech Mono', monospace", marginTop: 2, paddingLeft: 2 }}>
                    {li.policyNumber && <span>#{li.policyNumber}</span>}
                    {li.term && <span style={{ marginLeft: 8 }}>{li.term}</span>}
                  </div>
                  <textarea value={li.description} onChange={e => updateDescription(idx, e.target.value)}
                    rows={2} title="Line description printed on the invoice"
                    style={{ ...FIELD_STYLE, fontSize: 11, padding: '4px 6px', marginTop: 4, resize: 'vertical' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <span style={{ color: '#d4a657', fontSize: 13, fontFamily: "'Share Tech Mono', monospace" }}>$</span>
                  <input type="number" value={li.amount} onChange={e => updateAmount(idx, e.target.value)}
                    style={{ width: 90, background: '#111', border: '1px solid #2a2a2a', borderRadius: 3, color: '#d4a657', fontFamily: "'Share Tech Mono', monospace", fontSize: 13, fontWeight: 700, padding: '4px 6px', textAlign: 'right', outline: 'none' }} />
                </div>
                <button onClick={() => removeLine(idx)} title="Remove line" style={{ background: 'none', border: 'none', color: '#555', fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
              </div>
            ))
          ) : (
            /* ---- ITEMIZED / CONSOLIDATED VIEW ---- */
            lineItems.map((li, idx) => {
              const b = li.breakdown || {};
              const c = displayMode === 'consolidated' ? consolidateBreakdown(b) : null;
              const subRows = (c ? [
                { label: 'Premium', value: c.premium },
                { label: 'State Tax', value: c.stateTax },
                { label: 'Policy Fees', value: c.fees },
              ] : [
                { label: 'Premium', value: b.premium },
                { label: 'State Tax', value: b.stateTax },
                { label: 'Stamping Fee', value: b.stampingFee },
                { label: 'Inspection Fee', value: b.inspectionFee },
                { label: 'Policy Fee', value: b.policyFee },
                { label: 'Agency Fee', value: b.agencyFee },
                { label: 'Broker Fee', value: b.brokerFee },
                ...(b.additionalFees || []).filter(f => f.amount > 0).map(f => ({ label: f.label, value: f.amount })),
              ]).filter(r => r.value > 0);
              return (
                <div key={li.policyId} style={{ marginBottom: idx < lineItems.length - 1 ? 12 : 0 }}>
                  {/* Policy group header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      background: '#1a1a2e', border: '1px solid #333', borderRadius: 3,
                      padding: '1px 6px', fontSize: 9, fontWeight: 700, color: '#aaa',
                      fontFamily: "'Share Tech Mono', monospace", letterSpacing: '0.5px',
                    }}>{li.lob}</span>
                    <span style={{ fontSize: 12, color: '#ccc', fontFamily: "'Share Tech Mono', monospace", fontWeight: 600 }}>
                      {(li.carrier || '').split(/[\s,]+/).slice(0, 2).join(' ')}
                    </span>
                    <span style={{ fontSize: 10, color: '#666', fontFamily: "'Share Tech Mono', monospace" }}>
                      {li.policyNumber && `#${li.policyNumber}`} {li.term && `(${li.term})`}
                    </span>
                    <span style={{ flex: 1 }} />
                    <button onClick={() => removeLine(idx)} title="Remove" style={{ background: 'none', border: 'none', color: '#555', fontSize: 14, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
                  </div>
                  <textarea value={li.description} onChange={e => updateDescription(idx, e.target.value)}
                    rows={2} title="Line description printed on the invoice"
                    style={{ ...FIELD_STYLE, fontSize: 11, padding: '4px 6px', marginBottom: 4, resize: 'vertical' }} />
                  {/* Sub-rows */}
                  {subRows.map((sr, si) => (
                    <div key={si} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0 2px 20px' }}>
                      <span style={{ fontSize: 10, color: '#777', fontFamily: "'Share Tech Mono', monospace" }}>{sr.label}</span>
                      <span style={{ fontSize: 10, color: '#999', fontFamily: "'Share Tech Mono', monospace" }}>${sr.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                  {/* Subtotal */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #2a2a2a', marginTop: 4, paddingTop: 4, paddingLeft: 20 }}>
                    <span style={{ fontSize: 10, color: '#aaa', fontFamily: "'Share Tech Mono', monospace", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Subtotal</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <span style={{ color: '#d4a657', fontSize: 12, fontFamily: "'Share Tech Mono', monospace" }}>$</span>
                      <input type="number" value={li.amount} onChange={e => updateAmount(idx, e.target.value)}
                        style={{ width: 85, background: '#111', border: '1px solid #2a2a2a', borderRadius: 3, color: '#d4a657', fontFamily: "'Share Tech Mono', monospace", fontSize: 12, fontWeight: 700, padding: '3px 5px', textAlign: 'right', outline: 'none' }} />
                    </div>
                  </div>
                  {idx < lineItems.length - 1 && <div style={{ borderBottom: '1px solid #1a1a2e', marginTop: 8 }} />}
                </div>
              );
            })
          )}
        </div>

        {/* Grand total */}
        {!loading && lineItems.length > 0 && (
          <div style={{
            borderTop: '1px solid #2a2a2a', padding: '10px 16px',
            display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 12,
          }}>
            <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Grand Total
            </span>
            <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 16, color: '#d4a657', fontWeight: 700 }}>
              ${grandTotal.toLocaleString()}
            </span>
          </div>
        )}

        {/* Footer buttons */}
        <div style={{
          borderTop: '1px solid #2a2a2a', padding: '12px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <button onClick={onBack} style={{
            background: '#1a1a1a', border: '1px solid #333', borderRadius: 4,
            color: '#aaa', fontWeight: 600, fontSize: 11, padding: '8px 16px', cursor: 'pointer',
          }}>BACK</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              background: '#1a1a1a', border: '1px solid #333', borderRadius: 4,
              color: '#aaa', fontWeight: 600, fontSize: 11, padding: '8px 16px', cursor: 'pointer',
            }}>CANCEL</button>
            <button disabled={lineItems.length === 0 || loading || generating} onClick={handleGenerate} style={{
              background: lineItems.length > 0 && !loading && !generating ? '#d4a657' : '#333',
              border: '1px solid ' + (lineItems.length > 0 && !loading && !generating ? '#d4a657' : '#444'),
              borderRadius: 4, color: lineItems.length > 0 && !loading && !generating ? '#0f0f0f' : '#666',
              fontWeight: 700, fontSize: 11, padding: '8px 20px',
              cursor: lineItems.length > 0 && !loading && !generating ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s, color 0.15s',
            }}>{generating ? 'GENERATING...' : 'GENERATE'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
